import React, { useState, useEffect, useRef } from 'react'


// --- CONSTANTES DE RECURSOS ---
// Reemplaza estas rutas con las ubicaciones reales en tu carpeta public/assets
const ASSETS = {
    GALLO_NORMAL: '/img/games/pluckingTheRooster/Gallo1.png',
    GALLO_HIT: '/img/games/pluckingTheRooster/Gallo2.png',
    GALLO_BONUS_1: '/img/games/pluckingTheRooster/Gallo3.png',
    GALLO_BONUS_2: '/img/games/pluckingTheRooster/Gallo4.png',
    GALLO_BONUS_3: '/img/games/pluckingTheRooster/Gallo5.png',
    AUDIO_HIT: '/img/games/pluckingTheRooster/hit.mp3', // Opcional si agregas sonido a futuro
};

const INITIAL_GAME_DURATION = 15; // Segundos

const PluckingRooster = ({ onGameFinish, initialDuration = INITIAL_GAME_DURATION }) => {
    
    // --- ESTADOS DEL JUEGO ---
    const [gameState, setGameState] = useState('countdown'); // countdown, playing, bonus, finished
    const [score, setScore] = useState(0);
    const [countdown, setCountdown] = useState(3);
    const [activeHoles, setActiveHoles] = useState(Array(6).fill(false)); // Qué hoyos tienen gallos arriba
    const [hitHoles, setHitHoles] = useState(Array(6).fill(false)); // Cuáles han sido golpeados (para cambiar img)
    const [bonusImage, setBonusImage] = useState(ASSETS.GALLO_BONUS_1);
    
    // --- REFS PARA LOGICA DEL GAME LOOP (Evitan re-renders innecesarios) ---
    const gameTimerRef = useRef(null);      // Timer principal del juego
    const peepTimeoutRef = useRef(null);    // Timer para esconder/sacar gallos
    const lastHoleRef = useRef(-1);         // Último hoyo usado para no repetir inmediato
    const scoreRef = useRef(0);             // Referencia mutable del score para callbacks síncronos
    const isGameActiveRef = useRef(false);  // Flag crítica para el loop de peep
    const containerRef = useRef(null);      // Para añadir efectos visuales al DOM

    const hasFinishedRef = useRef(false);

    // --- EFECTOS VISUALES TEMPORALES (FLASH) ---
    const [flashType, setFlashType] = useState(null); // 'hit', 'miss' o null

    // --- INICIO: COUNTDOWN ---
    useEffect(() => {
        if (gameState === 'countdown') {
            const timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        startGame();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [gameState]);

    // --- LÓGICA: INICIAR JUEGO ---
    const startGame = () => {
        setGameState('playing');
        setScore(0);
        scoreRef.current = 0;
        isGameActiveRef.current = true;
        
        // Iniciar el ciclo de topos (gallos)
        peep();

        // Iniciar Timer Principal
        let timeLeft = initialDuration;
        let elapsed = 0;
        
        gameTimerRef.current = setInterval(() => {
            timeLeft--;
            elapsed++;

            // Lógica de "MÁS RÁPIDO" cada 5 segundos (solo visual o ajustar dificultad)
            if (elapsed % 5 === 0 && timeLeft > 0) {
                spawnFloatingAlert("¡MÁS RÁPIDO!", "#ef4444");
                // Aquí podrías reducir los tiempos del peep si quisieras
            }

            if (timeLeft <= 0) {
                startBonusPhase();
            }
        }, 1000);
    };

    // --- LÓGICA: MOTOR DE APARICIÓN (PEEP) ---
    const peep = () => {
        if (!isGameActiveRef.current) return;

        // Tiempos aleatorios (entre 0.7s y 1.1s)
        const time = Math.round(Math.random() * (1100 - 700) + 700);
        
        // Elegir hoyo aleatorio distinto al anterior
        const holeIndex = getRandomHoleIndex();
        
        // Activar Gallo
        activateHole(holeIndex, true);

        peepTimeoutRef.current = setTimeout(() => {
            // Desactivar Gallo si sigue activo
            activateHole(holeIndex, false);
            
            // Si el juego sigue, llamar recursivamente
            if (isGameActiveRef.current) {
                peep();
            }
        }, time);
    };

    const getRandomHoleIndex = () => {
        let newIndex;
        do {
            newIndex = Math.floor(Math.random() * 6);
        } while (newIndex === lastHoleRef.current);
        
        lastHoleRef.current = newIndex;
        return newIndex;
    };

    const activateHole = (index, isActive) => {
        setActiveHoles(prev => {
            const copy = [...prev];
            copy[index] = isActive;
            return copy;
        });
        
        // Si se desactiva (baja), reseteamos el estado de "golpeado" para la próxima
        if (!isActive) {
            setHitHoles(prev => {
                const copy = [...prev];
                copy[index] = false;
                return copy;
            });
        }
    };

    // --- LÓGICA: TRANSICIÓN A BONUS ---
    const startBonusPhase = () => {
        clearInterval(gameTimerRef.current);
        clearTimeout(peepTimeoutRef.current);
        isGameActiveRef.current = false;
        
        // Limpiar tablero
        setActiveHoles(Array(6).fill(false));
        
        spawnFloatingAlert("¡BONUS!", "#fbbf24");

        // Pequeño delay antes de mostrar la pantalla de Bonus
        setTimeout(() => {
            setGameState('bonus');
            
            // Cambiar imagen del gallo progresivamente
            setTimeout(() => setBonusImage(ASSETS.GALLO_BONUS_2), 2000);
            setTimeout(() => setBonusImage(ASSETS.GALLO_BONUS_3), 3000);

            // Terminar juego después de 4 segundos de bonus
            setTimeout(() => {
                endGame();
            }, 4000);
        }, 1000);
    };

    // --- LÓGICA: FIN DEL JUEGO ---
    // const endGame = () => {
    //     setGameState('finished');
    //     if (onGameFinish) {
    //         onGameFinish(scoreRef.current);
    //     }
    // };

    const endGame = () => {
        // SI YA TERMINÓ, NO HAGAS NADA (ROMPE EL BUCLE)
        if (hasFinishedRef.current) return;
        
        // MARCA COMO TERMINADO INMEDIATAMENTE
        hasFinishedRef.current = true; 
        
        // LIMPIEZA AGRESIVA DE TIMERS
        clearInterval(gameTimerRef.current);
        clearTimeout(peepTimeoutRef.current);
        
        setGameState('finished');
        
        // LLAMA AL PADRE SOLO UNA VEZ
        if (onGameFinish) {
            onGameFinish(scoreRef.current);
        }
    };

    // --- EVENT HANDLERS: INTERACCIÓN ---

    /**
     * Maneja el golpe exitoso a un gallo
     */
    const handleWhack = (e, index) => {
        e.stopPropagation(); // Evita que el click llegue al contenedor (que penaliza)

        if (gameState !== 'playing') return;
        if (!activeHoles[index]) return; // Si no está arriba, no cuenta
        if (hitHoles[index]) return;     // Si ya le pegaste, no cuenta doble

        // Actualizar Score
        updateScore(3);
        triggerFlash('hit');
        spawnHitText(e.clientX, e.clientY, "¡+3!");

        // Marcar como golpeado (cambia imagen) y bajarlo rápido
        setHitHoles(prev => {
            const copy = [...prev];
            copy[index] = true;
            return copy;
        });

        // Bajar el gallo visualmente un poco después
        setTimeout(() => activateHole(index, false), 500);
    };

    /**
     * Maneja el click fallido (tap en el fondo o hoyo vacío)
     */
    const handleMiss = (e) => {
        if (gameState === 'playing') {
            updateScore(-1);
            triggerFlash('miss');
        } else if (gameState === 'bonus') {
            // En bonus no resta puntos, pero flashea rojo
            triggerFlash('miss');
        }
    };

    /**
     * Maneja los taps en el Gallo Dorado (Bonus)
     */
    const handleBonusTap = (e) => {
        e.stopPropagation();
        if (gameState !== 'bonus') return;

        updateScore(1);
        triggerFlash('hit');
        spawnHitText(e.clientX, e.clientY, "¡+1!");
    };

    // --- HELPERS DE UTILIDAD ---

    const updateScore = (points) => {
        setScore(prev => {
            const newScore = Math.max(0, prev + points);
            scoreRef.current = newScore;
            return newScore;
        });
    };

    const triggerFlash = (type) => {
        setFlashType(null); // Reset forzar reflow si es necesario
        setTimeout(() => setFlashType(type), 10);
        setTimeout(() => setFlashType(null), 210); // Duración de animación CSS
    };

    // Genera elementos DOM temporales para rendimiento (mejor que estado React para partículas rápidas)
    const spawnHitText = (x, y, text) => {
        if (!containerRef.current) return;
        
        const el = document.createElement('div');
        el.className = 'rooster-game__hit-effect';
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
        el.innerText = text;
        
        containerRef.current.appendChild(el);
        setTimeout(() => el.remove(), 400); // Limpieza automática
    };

    const spawnFloatingAlert = (text, color) => {
        if (!containerRef.current) return;

        const el = document.createElement('div');
        el.className = 'rooster-game__status-alert';
        el.style.color = color;
        el.innerText = text;

        containerRef.current.appendChild(el);
        setTimeout(() => el.remove(), 1000);
    };

    // --- RENDER ---
    return (
        <div className="rooster-game" ref={containerRef}>
            
            {/* FLASH OVERLAYS */}
            <div className={`rooster-game__flash rooster-game__flash--hit ${flashType === 'hit' ? 'rooster-game__flash--active' : ''}`} />
            <div className={`rooster-game__flash rooster-game__flash--miss ${flashType === 'miss' ? 'rooster-game__flash--active' : ''}`} />

            {/* COUNTDOWN OVERLAY */}
            {gameState === 'countdown' && (
                <div className="rooster-game__overlay rooster-game__overlay--countdown">
                    <div className="rooster-game__countdown-text">{countdown}</div>
                </div>
            )}

            {/* BONUS STAGE OVERLAY */}
            {gameState === 'bonus' && (
                <div className="rooster-game__overlay rooster-game__overlay--bonus" onClick={handleMiss}>
                    <div className="rooster-game__tap-container">
                        {/* Generamos etiquetas TAP aleatorias decorativas */}
                        {[...Array(10)].map((_, i) => (
                            <div 
                                key={i} 
                                className="rooster-game__tap-label"
                                style={{
                                    left: `${Math.random() * 70 + 15}%`,
                                    top: `${Math.random() * 70 + 15}%`,
                                    animationDelay: `${Math.random() * 0.5}s`
                                }}
                            >
                                TAP
                            </div>
                        ))}
                    </div>
                    <div 
                        className="rooster-game__bonus-gallo" 
                        onPointerDown={handleBonusTap} // PointerDown es más rápido que Click en móviles
                    >
                        <img src={bonusImage} alt="Bonus Gallo" />
                    </div>
                </div>
            )}

            {/* GAME GRID (MAIN STAGE) */}
            <div className="rooster-game__main-wrapper" onPointerDown={handleMiss}>
                <div className="rooster-game__grid">
                    {activeHoles.map((isActive, index) => (
                        <div key={index} className="rooster-game__hole">
                            <div 
                                className={`rooster-game__rooster ${isActive ? 'rooster-game__rooster--active' : ''}`}
                                onPointerDown={(e) => handleWhack(e, index)}
                                data-hit={hitHoles[index]}
                            >
                                <img 
                                    src={hitHoles[index] ? ASSETS.GALLO_HIT : ASSETS.GALLO_NORMAL} 
                                    alt="Gallo" 
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* RESULT MODAL */}
            {gameState === 'finished' && (
                <div className="rooster-game__overlay rooster-game__overlay--result">
                    <div className="rooster-game__result-card">
                        <h2>¡TIEMPO!</h2>
                        <p>Puntuación Total</p>
                        <div className="rooster-game__score-final">{score}</div>
                        <p>Puntos</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export { PluckingRooster };